import type { IKeyedGroups } from "helpers/collections";
import type { RequestStatus } from "types/request";

export interface IBrandMenuState {
  menus: IKeyedGroups<IMenuRecord>;
  sections: IKeyedGroups<ISectionRecord>;
  items: IKeyedGroups<IItemRecord>;
  categories: IKeyedGroups<ICategory>;
  courses: IKeyedGroups<ICourse>;
  modifierGroups: IKeyedGroups<IModGroupRecord>;
  printerGroups: IKeyedGroups<IPrinterGroup>;
  taxRates: IKeyedGroups<ITaxRate>;
  requestStatus: {
    [index: string]: BrandMenuRequests;
  };
}

export type StoreIdList = string[];

export interface IPromisesByStoreId<T> {
  [index: string]: Promise<T>[];
}

export enum BrandMenuRequestKeys {
  GetMenus = "GetMenus",
  GetSections = "GetSections",
  GetMenuComponents = "GetMenuComponents",
  PostItem = "PostItem",
  PutItem = "PutItem",
  PostMenu = "PostMenu",
  PutMenu = "PutMenu",
  DeleteMenu = "DeleteMenu",
  PostOrPutItem = "PostOrPutItem",
}

export type BrandMenuRequests = {
  [key in BrandMenuRequestKeys]: RequestStatus;
};

// Hydrated interfaces that extend record returned from the backend.
// Hydration occurs in selectors/brandMenu.

interface IHQKeyInfo {
  storeId: string;
  storeName: string;
}

// Hydrated Menu interface includes the actual ISection records, the storeId,
// and sums up the items count.
export interface IMenu extends IMenuRecord, IHQKeyInfo {
  sections: ISection[];
  itemsCount: number;
}

// Hydrated interfaces includes storeId, storeName
export interface IItem extends IItemRecord, IHQKeyInfo {}
export interface ISection extends ISectionRecord, IHQKeyInfo {}
export interface IModGroup extends IModGroupRecord, IHQKeyInfo {}

// Record from the API, has references to child sections
export interface IMenuRecord {
  id: string;
  name: string;
  updatedAt: string;
  restaurantId: string;
  menuSections: ISectionReference[];
}

// When saving a Menu, pass field `menuSectionsAttributes` rather than menuSections
export interface IMenuSaveRecord extends Omit<IMenuRecord, "menuSections"> {
  menuSectionsAttributes: ISectionReference[];
}

// When saving an Item, pass ____Attributes fields. Omit the corresponding fields from IItemRecord
export interface IItemSaveRecord
  extends Omit<
    IItemRecord,
    | "itemModifierGroups"
    | "modifierGroups"
    | "restaurantId"
    | "sections"
    | "sides"
    | "taxRates"
  > {
  taxRatesAttributes: { taxRateId: string }[];
  sectionsAttributes: { sectionId: string; order: number }[];
  modifierGroupsAttributes: { modifierGroupId: string; order: number }[];
  sidesAttributes: Omit<ISide, "updatedAt">[];
}

export interface ISectionReference {
  id: string;
  order: number;
  sectionId: string;
  updatedAt: string;
  menuId: string;
}

export interface ISectionRecord {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  restaurantId: string;
  itemsCount: number;
  sections: ISectionRecord[];
  timingMask?: ITimingMask;
}

export interface ITimingMask {
  id: string;
  createdAt: string;
  startTime: string;
  endTime: string;
  startDate: string | null;
  endDate: string | null;
  rules: string[];
  status: string;
  ownerId: string;
}

export interface IMenuComponents {
  items: IItemRecord[];
  categories: ICategory[];
  courses: ICourse[];
  modifierGroups: IModGroupRecord[];
  printerGroups: IPrinterGroup[];
  taxRates: ITaxRate[];
}

export interface IItemRecord {
  id: string;
  name: string;
  allowPriceOverride: boolean;
  categoryId: string;
  chitName: string;
  cost: string;
  courseId: string;
  defaultOnDelivery: boolean;
  defaultOnTab: boolean;
  defaultOnTable: boolean;
  defaultOnToGo: boolean;
  description: string | null;
  feePercentage: string | null;
  fireImmediately: boolean;
  itemIdentifier: string | null;
  itemImages: IItemImage[];
  itemModifierGroups: IItemModGroupRecord[];
  itemType: number;
  maxSides: number;
  minSides: number;
  modifierGroups: string[];
  noPrint: boolean;
  price: string;
  receiptName: string;
  resetStockLevelTo: number | null;
  resetStockOnClose: number;
  restaurantId: string;
  sections: string[];
  sides?: ISide[];
  sortCode: string;
  stationId: string | null;
  status: number;
  stockLevel: number | null;
  taxRates: string[];
  updatedAt: string;
}

export interface IItemImage {
  id: string;
  itemId: string;
  userId: string;
  metadata: unknown;
  usedAs: number;
  thumbUrl: string;
}

export interface ISide {
  // This field refers to the id of the join record itself
  id: string;
  price: string;
  status: number;
  updatedAt: string;
  // Due to a quirk in pos-service serialization, Sides come through
  // with differing fields during GET vs POST operations.
  itemId?: string; // item id of the parent item
  sideId?: string; // item id of the child item
}

export interface ICategory {
  id: string;
  name: string;
  defaultTaxType: number;
  parentId: string;
  requiresPosition: boolean;
  stationId: string;
  status: number;
  updatedAt: string;
}

export interface ICourse {
  id: string;
  name: string;
  autoFire: boolean;
  isPeripheral: boolean;
  order: number | null;
  restaurantId: string;
  updatedAt: string;
}

export interface IModGroupRecord {
  id: string;
  name: string;
  maximumRequired: number;
  minimumRequired: number;
  modifiers: string[];
  nameLong: string;
  printName: boolean;
  restaurantId: string;
  status: number;
  updatedAt: string;
}

// item-mod group join record
export interface IItemModGroupRecord {
  id: string;
  itemId: string;
  modifierGroupId: string;
  order: number;
}

export interface IOrderedModGroup extends IModGroup {
  order: number;
}

export interface IPrinterGroup {
  id: string;
  name: string;
  kitchenDisplaySystems: string[];
  printCourselines: boolean;
  printers: string[];
  restaurantId: string;
  status: number;
  updatedAt: string;
}

export interface ITaxRate {
  id: string;
  name: string;
  displayPercent: string;
  rate: string;
  status: number;
  updatedAt: string;
}

// Counted tax rates are used on Item Create page.
// built in selector.
export interface ITaxRateCounted extends Pick<ITaxRate, "id"> {
  label: string;
  count: number;
}

export enum ItemReviewChangeType {
  ADD = "add",
  REMOVE = "remove",
  EDIT = "edit",
}

export interface IFieldOrderMap {
  field: keyof IItem;
  label: string;
}

export interface IChange {
  key: keyof IItem | "itemAdded";
  label: string;
  storeName: string;
  storeId: string;
  changes?: string;
  type: ItemReviewChangeType;
}
